import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { format } from 'date-fns';

import { PdfmakeService } from 'src/app/services';
import {
    CompoundTranslationType,
    CompoundTranslationTypeGroupKey,
    ScreeningFlag, ScreeningLocationDetailView,
    ScreeningLocationResultItem, ScreeningPolygonStatus
} from 'src/app/contracts';
import { LocationDetailsHelper } from './location-details.helper';

const win = window as any;

@Injectable()
export class ScreeningLocationPDFHelper {

    public flags: ScreeningFlag = 0;

    constructor(
        @Inject(LOCALE_ID) public locale,
        private readonly pdfmake: PdfmakeService,
        private readonly locationDetailsHelper: LocationDetailsHelper
    ) {

    }
    public exportPDF(
        locationNumber: string, locationName: string, pollutionStatusCode: string,
        screeningLocationDetails: ScreeningLocationDetailView,
        showV2: boolean, v2ScreeningResults: { [key: number]: Array<ScreeningLocationResultItem> },
        showV1: boolean, v1ScreeningResults: { [key: number]: Array<ScreeningLocationResultItem> }): void {
        this.pdfmake.initialize('A3');
        this.pdfmake.configureStyles(this.getPDFStyles());

        // Add header
        this.pdfmake.addDynamic(this.getPDFHeaderCols());

        // Location info
        this.pdfmake.addText(`Lokalitet: ${locationNumber}`, 'labelHeader');
        this.pdfmake.addText(locationName, 'contentHeader');

        if (showV2) {
            const factor = this.locationDetailsHelper
                .getNumberFormatWithDigitsInfo(screeningLocationDetails.v2PolygonResult.factor, '1.0-1');
            this.pdfmake.addText(this.locationDetailsHelper.getLeftBoxTitle(pollutionStatusCode), 'contentHeader');
            this.pdfmake.addText('Maksimal overskridelse:', 'screeningFactorLablel');
            this.pdfmake.addText(factor, 'screeningFactorText');

            this.pdfmake.addText('Risikovurdering:', 'screeningPolygonStatusLablel');
            if (screeningLocationDetails.v2PolygonResult.polygonStatus === ScreeningPolygonStatus.NoRisk) {
                this.pdfmake.addText('Ingen risiko', 'screeningPolygonStatusText');
            }
            else if (screeningLocationDetails.v2PolygonResult.polygonStatus === ScreeningPolygonStatus.Risk) {
                this.pdfmake.addText('Risiko', 'screeningPolygonStatusText');
            }
            else {
                this.pdfmake.addText('Olie- og benzinprodukter', 'screeningPolygonStatusText');
            }

            if (screeningLocationDetails.v2PolygonResult.reassessment) {
                this.pdfmake.addText('Revurderet af:', 'screeningPolygonStatusLablel');
                this.pdfmake.addText(screeningLocationDetails.v2PolygonResult.reassessment.caseWorker.name, 'screeningPolygonStatusText');
                this.pdfmake.addText(screeningLocationDetails.v2PolygonResult.reassessment.comment, 'screeningPolygonStatusText');
            }

            const first = screeningLocationDetails.v2Screenings[0];
            if (first) {
                this.pdfmake.addText('Dato for screening:', 'screeningPolygonStatusLablel');
                this.pdfmake.addText(this.locationDetailsHelper.getDateFormat(first.createdAt), 'screeningPolygonStatusText');
            }
        }
        if (showV1) {
            const factor = this.locationDetailsHelper
                .getNumberFormatWithDigitsInfo(screeningLocationDetails.v1PolygonResult.factor, '1.0-1');
            this.pdfmake.addText(this.locationDetailsHelper.getLeftBoxTitle(pollutionStatusCode), 'contentHeader');
            this.pdfmake.addText('Maksimal overskridelse:', 'screeningFactorLablel');
            this.pdfmake.addText(factor, 'screeningFactorText');

            this.pdfmake.addText('Risikovurdering:', 'screeningPolygonStatusLablel');
            if (screeningLocationDetails.v1PolygonResult.polygonStatus === ScreeningPolygonStatus.NoRisk) {
                this.pdfmake.addText('Ingen risiko', 'screeningPolygonStatusText');
            }
            else if (screeningLocationDetails.v1PolygonResult.polygonStatus === ScreeningPolygonStatus.Risk) {
                this.pdfmake.addText('Risiko', 'screeningPolygonStatusText');
            }
            else {
                this.pdfmake.addText('Olie- og benzinprodukter', 'screeningPolygonStatusText');
            }

            if (screeningLocationDetails.v1PolygonResult.reassessment) {
                this.pdfmake.addText('Revurderet af:', 'screeningPolygonStatusLablel');
                this.pdfmake.addText(screeningLocationDetails.v1PolygonResult.reassessment.caseWorker.name, 'screeningPolygonStatusText');
                this.pdfmake.addText(screeningLocationDetails.v1PolygonResult.reassessment.comment, 'screeningPolygonStatusText');
            }

            const first = screeningLocationDetails.v1Screenings[0];
            if (first) {
                this.pdfmake.addText('Dato for screening:', 'screeningPolygonStatusLablel');
                this.pdfmake.addText(this.locationDetailsHelper.getDateFormat(first.createdAt), 'screeningPolygonStatusText');
            }
        }

        this.pdfmake.addText('', 'seperateLine');
        this.pdfmake.addText('', 'seperateLine');

        this.addFlags(screeningLocationDetails);

        this.pdfmake.addText('', 'seperateLine');
        this.pdfmake.addText('', 'seperateLine');

        this.addScreeningResults('V2', v2ScreeningResults);
        this.addScreeningResults('V1', v1ScreeningResults);

        const fileName = 'Screening_Eksporter_' + locationNumber + '_' + format(new Date(), 'yyyyMMddHHmmss') + '.pdf';
        setTimeout(() => this.pdfmake.download(fileName), 50);
    }

    private addFlags(screeningLocationDetails: ScreeningLocationDetailView): void {
        this.pdfmake.addText('Flags:', 'screeningFlagLablel');
        let flags = ScreeningFlag.None;
        const allScreeningResults = screeningLocationDetails.v1Screenings.concat(screeningLocationDetails.v2Screenings);
        for (const screeningResult of allScreeningResults) {
            // tslint:disable-next-line: no-bitwise
            flags = flags | screeningResult.flag;
        }

        if (flags === ScreeningFlag.None) {
            this.pdfmake.addText('Ingen flag på denne lokalitet',
                'screeningFlagText');
        }

        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Bly_kobber_eller_PAH) === ScreeningFlag.Bly_kobber_eller_PAH) {
            this.pdfmake.addText('* Bly, kobber eller PAH fjernet i trin 1',
                'screeningFlagText');
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Kompleks_geologi) === ScreeningFlag.Kompleks_geologi) {
            this.pdfmake.addText('* Område med kompleks geologi',
                'screeningFlagText');
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Manglende_modelstof) === ScreeningFlag.Manglende_modelstof) {
            this.pdfmake.addText('* Et eller flere forureningsstoffer mangler et modelstof',
                'screeningFlagText');
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Svag_geologi) === ScreeningFlag.Svag_geologi) {
            this.pdfmake.addText('* Svag datadækning for bestemmelse af dæklagstykkelser',
                'screeningFlagText');
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.MTBE_fjernet) === ScreeningFlag.MTBE_fjernet) {
            this.pdfmake.addText('* MTBE fjernet', 'screeningFlagText');
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Boring_uden_ler) === ScreeningFlag.Boring_uden_ler) {
            this.pdfmake.addText('* Lokalitet har boring uden ler i en radius af 100-300 m fra kant',
                'screeningFlagText');
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Risiko_pga_vandindvinding) === ScreeningFlag.Risiko_pga_vandindvinding) {
            this.pdfmake.addText('* Lokalitet udgør en risiko fordi der ligger en vandindvindingsboring indenfor 100 m',
                'screeningFlagText');
        }
        // tslint:disable-next-line: no-bitwise
        if ((flags & ScreeningFlag.Kun_Olie_Benzin_Stoffer) === ScreeningFlag.Kun_Olie_Benzin_Stoffer) {
            this.pdfmake.addText('* Lokalitet er udelukkende forurenet med olie- og benzin stoffer',
                'screeningFlagText');
        }
    }

    private addScreeningResults(type: string, screeningResults: { [key: number]: Array<ScreeningLocationResultItem> }): void {
        const exportType = 'PDF';
        for (const resultKey of Object.keys(screeningResults)) {
            const num = parseFloat(resultKey);
            const title = `${type} - ${this.locationDetailsHelper.getNumberFormat(num)} m2 - Screeningsresultater`;
            this.pdfmake.addText(title, 'labelHeader');

            let body = [];

            // Group by CompoundTranslationType
            const screeningResultsByKey = screeningResults[resultKey];
            const activityScreeningResults = screeningResultsByKey
                .filter(el => el.compoundTranslationType === CompoundTranslationType.Activity);
            const pollutionCauseScreeningResults = screeningResultsByKey
                .filter(el => el.compoundTranslationType === CompoundTranslationType.PollutionCause);
            const pollutantScreeningResults = screeningResultsByKey
                .filter(el => el.compoundTranslationType === CompoundTranslationType.Pollutant);

            // Stoffer
            if (pollutantScreeningResults && pollutantScreeningResults.length > 0) {
                // Set width columns
                const header = this.locationDetailsHelper.getScreengResultTableHeader(CompoundTranslationTypeGroupKey.Stoffer);
                const widths = [];
                header.forEach(item =>  widths.push('auto'));

                this.pdfmake.addText('Stoffer', 'labelHeader');
                this.pdfmake.addText('', 'seperateLine');

                for (const result of pollutantScreeningResults) {
                    body.push(this.locationDetailsHelper.getRow(CompoundTranslationTypeGroupKey.Stoffer, result, exportType));
                }

                this.pdfmake.addTable(header, body, widths);
                this.pdfmake.addText('', 'seperateLine');
                body = [];
            }

            // Brancher
            if (pollutionCauseScreeningResults && pollutionCauseScreeningResults.length > 0) {
                // Set width columns
                const header = this.locationDetailsHelper.getScreengResultTableHeader(CompoundTranslationTypeGroupKey.Brancher);
                const widths = [];
                header.forEach(item => widths.push(item === 'Branche' ? 70 : 'auto'));

                this.pdfmake.addText('Brancher', 'labelHeader');
                this.pdfmake.addText('', 'seperateLine');

                for (const result of pollutionCauseScreeningResults) {
                    body.push(this.locationDetailsHelper.getRow(CompoundTranslationTypeGroupKey.Brancher, result, exportType));
                }

                this.pdfmake.addTable(header, body, widths);
                this.pdfmake.addText('', 'seperateLine');
                body = [];
            }

            // Aktiviteter
            if (activityScreeningResults && activityScreeningResults.length > 0) {
                // Set width columns
                const header = this.locationDetailsHelper.getScreengResultTableHeader(CompoundTranslationTypeGroupKey.Aktiviteter);
                const widths = [];
                header.forEach(item =>  widths.push(item === 'Aktiviteter' ? 70 : 'auto'));

                this.pdfmake.addText('Aktiviteter', 'labelHeader');
                this.pdfmake.addText('', 'seperateLine');

                for (const result of activityScreeningResults) {
                    body.push(this.locationDetailsHelper.getRow(CompoundTranslationTypeGroupKey.Aktiviteter, result, exportType));
                }

                this.pdfmake.addTable(header, body, widths);
                this.pdfmake.addText('', 'seperateLine');
            }
        }
    }

    private getPDFHeaderCols(): any {
        const headerCols = {
            columns: [
                {
                    width: '*',
                    stack: [
                        { text: 'GrundRisk - Resultater', style: 'header' },
                        { text: 'Danmarks Miljøportal', style: 'subheader' },
                    ]
                },
                {
                    width: 'auto',
                    text: format(new Date(), 'dd/MM/yyyy'),
                    margin: [0, 20, 0, 0]
                },

            ]
        };
        return headerCols;
    }

    private getPDFStyles(): any {
        // Configure text styles
        const styles = {
            header: {
                fontSize: 35,
                bold: true,
                color: '#444451'
            },
            subheader: {
                fontSize: 12,
                color: '#444451',
            },
            footer: {
                fontSize: 25,
                bold: true,
                color: 'blue'
            },
            labelHeader: {
                color: '#444451',
                fontSize: 14,
                italics: true,
                margin: [0, 25, 0, 0]
            },
            contentHeader: {
                fontSize: 16,
                color: '#444451',
                bold: true,
                italics: true,
                margin: [0, 0, 0, 25]
            },
            screeningFactorLablel: {
                color: '#444451',
                fontSize: 14,
                margin: [0, 0, 0, 0]
            },
            screeningFactorText: {
                color: '#b1114b',
                fontSize: 12,
                margin: [0, 0, 0, 0]
            },
            screeningPolygonStatusLablel: {
                color: '#444451',
                fontSize: 14,
                margin: [0, 0, 0, 0]
            },
            screeningPolygonStatusText: {
                fontSize: 12,
                bold: true,
                margin: [0, 0, 0, 0]
            },
            screeningFlagLablel: {
                color: '#444451',
                fontSize: 14,
                margin: [0, 0, 0, 0]
            },
            screeningFlagText: {
                fontSize: 12,
                italics: true,
                margin: [0, 0, 0, 0]
            },
            seperateLine: {
                margin: [0, 10, 0, 0]
            }
        };

        return styles;
    }
}
